import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Typography, Box } from "@material-ui/core";
import { COLORS } from "../constants";
import { allDescIds, findNode, getChain } from "../utils";
import { useStyles } from "./location-tree.style";

function collectMatchingIds(nodes, query) {
  const out = [];
  if (!query) return out;
  for (const node of nodes) {
    if (node.name.toLowerCase().includes(query)) out.push(node.id);
    if (node.children?.length) out.push(...collectMatchingIds(node.children, query));
  }
  return out;
}

function computeVisibleIds(locations, search) {
  const query = (search || "").trim().toLowerCase();
  if (!query) return null;
  const matchingIds = collectMatchingIds(locations, query);
  const visible = new Set();
  for (const id of matchingIds) {
    visible.add(id);
    const chain = getChain(locations, id);
    if (chain) chain.forEach((node) => visible.add(node.id));
    const node = findNode(locations, id);
    if (node) allDescIds(node).forEach((descId) => visible.add(descId));
  }
  return visible;
}

function hasPartialCheck(node, checked) {
  return (node.children || []).some(
    (child) => checked?.has(child.id) || (child.children || []).some((grandchild) => checked?.has(grandchild.id))
  );
}

function ExpandToggle({ isExpanded, onToggle, nodeId }) {
  const classes = useStyles();
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onToggle?.(nodeId);
      }}
      className={classes.expandToggle}
    >
      {isExpanded ? "▼" : "▶"}
    </button>
  );
}

ExpandToggle.propTypes = {
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  nodeId: PropTypes.string,
};

ExpandToggle.defaultProps = {
  isExpanded: false,
  onToggle: undefined,
  nodeId: undefined,
};

const LEVEL_ICONS = ["🌎", "🏛️", "🏢", "📍", "🛏️"];

export const LocationTree = ({
  locations,
  search,
  selected,
  onSelect,
  onCheck,
  checked,
  mode,
  expanded,
  onToggle,
}) => {
  const classes = useStyles();
  const visibleIds = useMemo(
    () => computeVisibleIds(locations, search),
    [locations, search]
  );

  const renderNode = (node, depth = 0) => {
    const query = (search || "").toLowerCase();
    if (query && visibleIds && !visibleIds.has(node.id)) return null;
    const isExpanded = expanded?.has(node.id) || !!query;
    const hasChildren = node.children?.length > 0;
    const isSelected = selected === node.id;
    const isChecked = checked?.has(node.id);
    const descendantIds = allDescIds(node);
    const isPartiallyChecked = !isChecked && hasPartialCheck(node, checked);

    return (
      <Box key={node.id}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: "5px 8px",
            paddingLeft: `${8 + depth * 20}px`,
            background: isSelected ? COLORS.primaryLight : "transparent",
            borderRadius: '4px',
            marginBottom: '1px',
            borderLeft: isSelected ? `3px solid ${COLORS.primary}` : "3px solid transparent",
            textAlign: "left",
          }}
        >
          {hasChildren && (
            <ExpandToggle isExpanded={isExpanded} onToggle={onToggle} nodeId={node.id} />
          )}
          {!hasChildren && <Box sx={{ width: 18, flexShrink: 0 }} />}
          {mode === "select" && (
            <input
              type="checkbox"
              checked={!!isChecked}
              ref={(element) => {
                if (element) element.indeterminate = !!isPartiallyChecked && !isChecked;
              }}
              onChange={() => onCheck?.(node.id, descendantIds)}
              onClick={(event) => event.stopPropagation()}
              style={{ marginRight: 6, accentColor: COLORS.primary, flexShrink: 0 }}
            />
          )}
          {mode === "browse" && (
            <button
              type="button"
              onClick={() => onSelect?.(node.id)}
              className={classes.nodeButton}
            >
              <Typography component="span" className={classes.nodeIcon}>{LEVEL_ICONS[node.level]}</Typography>
              <Typography
                component="span"
                variant="body2"
                align="left"
                sx={{
                  color: COLORS.body,
                  fontWeight: isSelected ? 600 : 400,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {node.name}
              </Typography>
            </button>
          )}
          {mode !== "browse" && (
            <>
              <Typography component="span" className={classes.nodeIcon}>{LEVEL_ICONS[node.level]}</Typography>
              <Typography
                component="span"
                variant="body2"
                align="left"
                sx={{
                  color: COLORS.body,
                  fontWeight: isSelected ? 600 : 400,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {node.name}
              </Typography>
            </>
          )}
        </Box>
        {(isExpanded || query) && (node.children || []).map((child) => renderNode(child, depth + 1))}
      </Box>
    );
  };

  return <Box>{locations.map((location) => renderNode(location))}</Box>;
};

LocationTree.propTypes = {
  locations: PropTypes.array.isRequired,
  search: PropTypes.string,
  selected: PropTypes.string,
  onSelect: PropTypes.func,
  onCheck: PropTypes.func,
  checked: PropTypes.instanceOf(Set),
  mode: PropTypes.string,
  expanded: PropTypes.instanceOf(Set),
  onToggle: PropTypes.func,
};

LocationTree.defaultProps = {
  search: "",
  selected: undefined,
  onSelect: undefined,
  onCheck: undefined,
  checked: undefined,
  mode: "browse",
  expanded: undefined,
  onToggle: undefined,
};
